import { parseISO } from 'date-fns';
import { ChangeEvent } from 'react';
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';

import {
  Service,
  ServiceMeterDemandUnits,
  ServiceTypesForBulkDelivery,
  ServiceUsageUnits,
} from 'common/src/types/enums/Service.enum';
import { IAccount, IFacility, IMeter } from 'common/src/types/interfaces';

import { getStartAndEndDateFields } from 'portal/src/components/common/forms/commonFormFields';
import getInternalNotesField from 'portal/src/components/common/forms/internalNotesField';
import generateAllocationInfoTooltip from 'portal/src/components/common/generateAllocationInfoTooltip';

import {
  autoCompleteType,
  datePickerType,
  noFieldType,
  numberType,
  selectInputType,
  switchType,
  textInputType,
} from '../../../common/forms/FormFieldTypes';

export type MeterFormValues = ReturnType<typeof defaultValues>;

export const defaultValues = (entity: Partial<IMeter>) => ({
  activeDate: entity.activeDate ? parseISO(entity.activeDate) : null,
  associatedRenewableMeterId: entity.associatedRenewableMeterId ?? null,
  automationMeterName: entity.automationMeterName ?? '',
  bulkDelivery: !!entity.bulkDelivery,
  chargesOnly: !!entity.chargesOnly,
  code: entity.code ?? '',
  demandUnit: entity.demandUnit ?? null,
  glCode: entity.glCode ?? '',
  inactiveDate: entity.inactiveDate ? parseISO(entity.inactiveDate) : null,
  isMaster: !!entity.isMaster,
  masterMeterId: entity.masterMeterId ?? null,
  multiplier: entity.multiplier ?? null,
  name: entity.name ?? '',
  netMeter: !!entity.netMeter,
  internalNotes: entity.internalNotes ?? '',
  notes: entity.notes ?? '',
  parentAccountId: entity.parentAccountId ?? null,
  parentFacilityId: entity.parentFacilityId ?? null,
  rateSchedule: entity.rateSchedule ?? '',
  replacesMeterId: entity.replacesMeterId ?? null,
  serialNumber: entity.serialNumber ?? '',
  serviceType: entity.serviceType ?? null,
  smartMeter: !!entity.smartMeter,
  usageUnit: entity.usageUnit ?? null,
});

const ACTIVE_ALLOCATION_TOOLTIP =
  'Warning: this meter is used in an allocation. Changing this value may cause errors when allocating bills.';

const MASTER_METER_TOOLTIP =
  "When enabled, bill line items for this meter will be allocated to sub-meters. Master meters must have one sub-meter assigned to each of it's parent account's sub-accounts.";

const SUB_METER_TOOLTIP =
  "When set, this meter will be designated as a sub-meter for use in allocated bills. The master meter must be associated with the parent account's master accounts.";

export const getFields = (
  accounts: IAccount[] = [],
  facilities: IFacility[] = [],
  meters: IMeter[] = [],
  netMeter: boolean,
  service: Service,
  hasSystemAdminAccess: boolean,
  showMasterMeterField: boolean,
  masterMeters: IMeter[] = [],
  onRateScheduleChange: (event: ChangeEvent<HTMLInputElement>) => void,
  getValues: UseFormGetValues<MeterFormValues>,
  setValue: UseFormSetValue<MeterFormValues>,
  hasActiveAllocationTemplate: boolean,
  openHistoricalValues?: () => void,
  allocationsEnabled: boolean = true,
) => {
  const serviceHasDemandUnits = !!ServiceMeterDemandUnits[service];

  const formFieldsConfiguration: (
    | autoCompleteType
    | datePickerType
    | noFieldType
    | numberType
    | selectInputType
    | switchType
    | textInputType
  )[] = [
    {
      placeholder: 'Meter Name',
      label: 'Meter Name',
      name: 'name',
      type: 'textInput',
      required: true,
    },
    {
      placeholder: 'Service Type',
      label: 'Service Type',
      name: 'serviceType',
      onChange: (value: string) => {
        const serviceType = value as Service;

        if (serviceType) {
          const { demandUnit, usageUnit } = getValues();

          if (!ServiceUsageUnits[serviceType].includes(usageUnit)) {
            setValue('usageUnit', null, { shouldDirty: true });
          }

          if (
            !ServiceMeterDemandUnits[serviceType] ||
            !ServiceMeterDemandUnits[serviceType].includes(demandUnit)
          ) {
            setValue('demandUnit', null, { shouldDirty: true });
          }

          if (serviceType !== Service.Electric) {
            setValue('netMeter', false, { shouldDirty: true });
            setValue('associatedRenewableMeterId', null, { shouldDirty: true });
          }

          if (!ServiceTypesForBulkDelivery.includes(serviceType)) {
            setValue('bulkDelivery', false, { shouldDirty: true });
          }
        }
      },
      type: 'selectInput',
      required: true,
      values: Object.values(Service).map((item) => ({
        label: item,
        value: item,
      })),
    },
    {
      placeholder: 'Usage Unit',
      label: 'Usage Unit',
      name: 'usageUnit',
      type: 'selectInput',
      required: true,
      values: !service
        ? []
        : ServiceUsageUnits[service].map((item) => ({
            label: item,
            value: item,
          })),
    },
    {
      placeholder: 'Demand Unit',
      label: 'Demand Unit',
      name: 'demandUnit',
      type: 'selectInput',
      required: serviceHasDemandUnits,
      values: !serviceHasDemandUnits
        ? []
        : ServiceMeterDemandUnits[service].map((item) => ({
            label: item,
            value: item,
          })),
      hidden: !serviceHasDemandUnits,
    },
    {
      type: 'noField',
      name: 'noField',
      hidden: serviceHasDemandUnits,
    },
    {
      infoTooltip: hasActiveAllocationTemplate
        ? ACTIVE_ALLOCATION_TOOLTIP
        : undefined,
      label: 'Parent Account',
      name: 'parentAccountId',
      options: accounts.map((item) => ({
        label: item.number,
        value: item.id,
        disabled: !item.isActive,
      })),
      placeholder: 'Parent Account',
      required: true,
      type: 'autocomplete',
    },
    {
      placeholder: 'Parent Facility',
      label: 'Parent Facility',
      name: 'parentFacilityId',
      type: 'autocomplete',
      required: true,
      options: facilities.map((item) => ({
        label: item.name,
        value: item.id,
        key: item.id,
        disabled: !item.isActive,
      })),
    },
    ...getStartAndEndDateFields({
      startFieldName: 'activeDate',
      endFieldName: 'inactiveDate',
      startLabel: 'Active Date',
      endLabel: 'Inactive Date',
    }),
    {
      label: 'Is this a net meter?',
      name: 'netMeter',
      type: 'switch',
      required: false,
      hidden: service !== Service.Electric,
    },
    {
      placeholder: 'Associated Renewable Meter',
      label: 'Associated Renewable Meter',
      name: 'associatedRenewableMeterId',
      type: 'autocomplete',
      required: false,
      options: meters
        .filter(({ serviceType }) => {
          return (
            serviceType === Service.OnSiteSolar ||
            serviceType === Service.OnSiteWind
          );
        })
        .map((item) => ({
          label: item.name,
          value: item.id,
          key: item.id,
          disabled: !item.isActive,
        })),
      hidden: !netMeter,
    },
    {
      type: 'noField',
      name: 'noField',
      hidden: service !== Service.Electric || netMeter,
    },
    {
      label: 'Is this a smart meter?',
      name: 'smartMeter',
      type: 'switch',
      required: false,
      prependDivider: service === Service.Electric,
    },
    {
      label: 'Does this meter have bulk delivery?',
      name: 'bulkDelivery',
      type: 'switch',
      required: false,
      hidden: !ServiceTypesForBulkDelivery.includes(service),
    },
    {
      type: 'noField',
      name: 'noField',
      hidden: ServiceTypesForBulkDelivery.includes(service),
    },
    {
      placeholder: 'Meter Code',
      label: 'Meter Code',
      name: 'code',
      type: 'textInput',
      required: false,
    },
    {
      placeholder: 'Meter Serial Number',
      label: 'Meter Serial Number',
      name: 'serialNumber',
      type: 'textInput',
      required: false,
    },
    {
      placeholder: 'Meter Multiplier',
      label: 'Meter Multiplier',
      name: 'multiplier',
      type: 'number',
      required: false,
    },
    {
      label: 'Does the meter incur charges only?',
      name: 'chargesOnly',
      type: 'switch',
      required: false,
    },
    {
      placeholder: 'Replaces Meter',
      label: 'Replaces Meter',
      name: 'replacesMeterId',
      type: 'autocomplete',
      required: false,
      options: meters.map((item) => ({
        disabled: !item.isActive,
        label: item.name,
        value: item.id,
      })),
    },
    {
      placeholder: 'G/L Code',
      label: 'G/L Code',
      name: 'glCode',
      type: 'textInput',
      required: false,
    },
    {
      placeholder: 'Rate Schedule',
      label: 'Rate Schedule',
      name: 'rateSchedule',
      type: 'textInput',
      required: false,
      onChange: onRateScheduleChange,
      helperAction: openHistoricalValues
        ? {
            label: 'See changes',
            onClick: openHistoricalValues,
          }
        : null,
    },
    {
      placeholder: 'Automation Meter Name',
      label: 'Automation Meter Name',
      name: 'automationMeterName',
      type: 'textInput',
      required: false,
      hidden: !hasSystemAdminAccess,
    },
    {
      placeholder: 'Notes',
      label: 'Notes',
      multiline: true,
      name: 'notes',
      type: 'textInput',
      required: false,
    },
    getInternalNotesField(hasSystemAdminAccess),
    {
      type: 'noField',
      name: 'noField',
    },
    {
      disabled: !allocationsEnabled,
      infoTooltip: generateAllocationInfoTooltip(
        allocationsEnabled,
        hasActiveAllocationTemplate,
        ACTIVE_ALLOCATION_TOOLTIP,
        MASTER_METER_TOOLTIP,
      ),
      label: 'Is this a master meter?',
      name: 'isMaster',
      onChange: (newValue: string) => {
        if (Boolean(newValue)) {
          setValue('masterMeterId', null, { shouldDirty: true });
        }
      },
      required: false,
      type: 'switch',
      prependDivider: 'Allocations',
    },
    {
      disabled: !allocationsEnabled,
      hidden: !showMasterMeterField,
      infoTooltip: generateAllocationInfoTooltip(
        allocationsEnabled,
        hasActiveAllocationTemplate,
        ACTIVE_ALLOCATION_TOOLTIP,
        SUB_METER_TOOLTIP,
      ),
      label: 'Master Meter',
      name: 'masterMeterId',
      options: masterMeters.map((item) => ({
        disabled: !item.isActive,
        key: item.id,
        label: item.name,
        value: item.id,
      })),
      placeholder: 'Master Meter',
      required: false,
      type: 'autocomplete',
    },
  ];

  return formFieldsConfiguration;
};
